/* ============================================================
   MapLibre → AMap adapter
   Zamenjuje AMap (kineski servis, GCJ02) besplatnom satelitskom
   mapom (MapLibre + Esri World Imagery, WGS84), a zadržava
   AMap-kompatibilan API koji postojeći kod (markeri, putanje)
   već koristi — tako da se ostatak aplikacije ne menja.
   ============================================================ */
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { ldsSatelliteStyle, SERBIA_CENTER } from '/@/utils/lds-map-style'

// re-export (postojeći kod importuje odavde)
export { SERBIA_CENTER }

/* ---------- Pomoćne AMap klase ---------- */
export class Size {
  width: number
  height: number
  constructor (w: number, h: number) {
    this.width = w
    this.height = h
  }
}

export class LngLat {
  lng: number
  lat: number
  constructor (lng: number, lat: number) {
    this.lng = lng
    this.lat = lat
  }
  getLng () { return this.lng }
  getLat () { return this.lat }
}

export class Icon {
  image: string
  size: Size
  constructor (opts: { image: string, imageSize?: Size, size?: Size }) {
    this.image = opts.image
    this.size = opts.imageSize || opts.size || new Size(40, 40)
  }
}

/* ---------- Marker ---------- */
export class Marker {
  private _map: LDSMap | null = null
  private _mlMarker: maplibregl.Marker | null = null
  private _lngLat: [number, number]
  private _el: HTMLDivElement
  private _icon?: Icon
  private _title: string
  private _extData: any = {}
  private _anim: number | null = null

  constructor (opts: {
    position?: LngLat | [number, number]
    icon?: Icon
    title?: string
    anchor?: string
    offset?: [number, number]
  }) {
    const pos = opts.position
    if (pos instanceof LngLat) this._lngLat = [pos.lng, pos.lat]
    else if (Array.isArray(pos)) this._lngLat = [pos[0], pos[1]]
    else this._lngLat = SERBIA_CENTER

    this._icon = opts.icon
    this._title = opts.title || ''

    // HTML element markera (ikonica + suptilan sjaj)
    const el = document.createElement('div')
    el.className = 'lds-map-marker'
    el.title = this._title
    if (this._icon) {
      const img = document.createElement('img')
      img.src = this._icon.image
      img.style.width = this._icon.size.width + 'px'
      img.style.height = this._icon.size.height + 'px'
      img.draggable = false
      el.appendChild(img)
    }
    this._el = el
  }

  _attach (map: LDSMap) {
    this._map = map
    this._mlMarker = new maplibregl.Marker({ element: this._el, anchor: 'bottom' })
      .setLngLat(this._lngLat)
      .addTo(map.raw)
  }

  _detach () {
    if (this._anim) cancelAnimationFrame(this._anim)
    this._mlMarker?.remove()
    this._mlMarker = null
    this._map = null
  }

  getPosition () { return new LngLat(this._lngLat[0], this._lngLat[1]) }

  setPosition (pos: LngLat | [number, number]) {
    this._lngLat = pos instanceof LngLat ? [pos.lng, pos.lat] : [pos[0], pos[1]]
    this._mlMarker?.setLngLat(this._lngLat)
  }

  setExtData (data: any) { this._extData = data }
  getExtData () { return this._extData }

  on () { /* događaji markera se ne koriste u osnovnom toku */ }

  // AMap-kompatibilna animirana promena pozicije
  moveTo (target: [number, number], opts?: { duration?: number, autoRotation?: boolean }) {
    if (!this._mlMarker) return
    const duration = opts?.duration ?? 1000
    const from: [number, number] = [this._lngLat[0], this._lngLat[1]]
    const to: [number, number] = [target[0], target[1]]
    const start = performance.now()
    if (this._anim) cancelAnimationFrame(this._anim)
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      const lng = from[0] + (to[0] - from[0]) * t
      const lat = from[1] + (to[1] - from[1]) * t
      this._lngLat = [lng, lat]
      this._mlMarker!.setLngLat(this._lngLat)
      if (t < 1) this._anim = requestAnimationFrame(step)
    }
    this._anim = requestAnimationFrame(step)
  }
}

/* ---------- Polyline (putanja leta) ---------- */
let polylineSeq = 0
export class Polyline {
  private _map: LDSMap | null
  private _id: string
  private _color: string
  private _path: [number, number][] = []

  constructor (opts: { map?: LDSMap, strokeColor?: string }) {
    this._map = opts.map || null
    this._color = opts.strokeColor || '#00FBF4'
    this._id = 'lds-track-' + (polylineSeq++)
    if (this._map) this._ensureLayer()
  }

  private _ensureLayer () {
    const map = this._map?.raw
    if (!map || !map.isStyleLoaded()) {
      this._map?.raw.once('styledata', () => this._ensureLayer())
      return
    }
    if (map.getSource(this._id)) return
    map.addSource(this._id, {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: this._path } }
    })
    map.addLayer({
      id: this._id,
      type: 'line',
      source: this._id,
      paint: {
        'line-color': this._color,
        'line-width': 3,
        'line-opacity': 0.9
      }
    })
  }

  setPath (path: [number, number][]) {
    this._path = path || []
    const map = this._map?.raw
    const src = map?.getSource(this._id) as maplibregl.GeoJSONSource | undefined
    if (src) {
      src.setData({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: this._path } })
    }
  }
}

/* ---------- MouseTool (crtanje — zasad stub, dodajemo kasnije) ---------- */
export class MouseTool {
  constructor (_map: LDSMap) { /* noop */ }
  marker () { /* noop */ }
  polyline () { /* noop */ }
  polygon () { /* noop */ }
  rectangle () { /* noop */ }
  circle () { /* noop */ }
  close () { /* noop */ }
  on () { /* noop */ }
}

/* ---------- Map ---------- */
export class LDSMap {
  raw: maplibregl.Map

  constructor (container: string, opts: { center?: [number, number], zoom?: number }) {
    this.raw = new maplibregl.Map({
      container,
      style: ldsSatelliteStyle(),
      center: opts.center || SERBIA_CENTER,
      zoom: opts.zoom ?? 7,
      attributionControl: false
    })
    this.raw.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'bottom-right')
    this.raw.addControl(new maplibregl.AttributionControl({ compact: true }))
  }

  add (marker: Marker) { marker._attach(this) }
  remove (marker: Marker) { if (marker && marker._detach) marker._detach() }
  setCenter (c: [number, number]) { this.raw.setCenter(c) }
  setZoom (z: number) { this.raw.setZoom(z) }
  setFitView () { /* opciono kasnije */ }
  destroy () { this.raw.remove() }
}

/* ---------- AMap-kompatibilan namespace ---------- */
export const AMapCompat = {
  Map: LDSMap,
  Marker,
  Icon,
  Size,
  LngLat,
  Polyline,
  MouseTool,
}

export default AMapCompat
